import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { GBP, pretty, ALLERGEN_RELATIONS, ALLERGEN_STATUSES } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, AlertTriangle, ShieldCheck, Tag, Star, Sparkles, GripVertical, GripHorizontal, Image as ImageIcon, Loader2, Wand2 } from "lucide-react";
import { uploadFiles, fileUrl } from "@/lib/upload";
import ImageCropper from "@/components/ImageCropper";
import MenuScanDialog from "@/components/MenuScanDialog";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, rectSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableCategory({ id, name, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.9 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <button ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`drag-category-${id}`}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground touch-none">
          <GripVertical className="h-5 w-5" />
        </button>
        <h3 className="font-display font-bold text-lg">{name}</h3>
      </div>
      {children}
    </div>
  );
}

function SortableMenuItem({ id, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined, opacity: isDragging ? 0.85 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <button ref={setActivatorNodeRef} {...attributes} {...listeners} data-testid={`drag-item-${id}`}
        className="absolute left-1/2 -translate-x-1/2 top-1 z-10 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-foreground touch-none">
        <GripHorizontal className="h-4 w-4" />
      </button>
      {children}
    </div>
  );
}

const empty = { name: "", description: "", price: 0, category_id: "", subcategory: "", prep_time_minutes: 15, image_url: "",
  is_available: true, allergen_note: "", allergen_links: [], options: [], band_prices: {},
  is_promotion: false, promotion_text: "", promotion_price: "", is_popular: false, is_cross_sell: false };

const PRICE_CONTEXTS = [
  ["DINE_IN", "Dine-in"],
  ["COLLECTION", "Collection / Takeaway"],
  ["DELIVERY", "Delivery"],
  ["WEBSITE", "Website"],
  ["AI_PHONE", "Phone (AI)"],
  ["THIRD_PARTY", "Third-party (HubRise/Deliverect)"],
];

function PriceBandsManager({ rid, bands, onChanged }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [contexts, setContexts] = useState([]);
  const [isDefault, setIsDefault] = useState(false);

  const toggleCtx = (c) => setContexts((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a band name");
    try {
      await api.post(`/restaurants/${rid}/price-bands`, { name: name.trim(), contexts, is_default: isDefault, sort_order: bands.length });
      setName(""); setContexts([]); setIsDefault(false); onChanged(); toast.success("Price band added");
    } catch { toast.error("Could not add band"); }
  };
  const del = async (bid) => { await api.delete(`/price-bands/${bid}`); onChanged(); toast.success("Band removed"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-11 gap-2" data-testid="manage-price-bands"><Tag className="h-4 w-4" /> Price bands {bands.length > 0 && `(${bands.length})`}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Channel price bands</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Create pricing bands and choose which channels each applies to. Then set per-item prices for each band in the item editor. Items with no band price fall back to the base price.</p>

        {bands.length > 0 && (
          <div className="space-y-2">
            {bands.map((b) => (
              <div key={b.id} data-testid={`price-band-${b.id}`} className="flex items-start justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="font-medium">{b.name} {b.is_default && <Badge variant="outline" className="ml-1 text-xs">default</Badge>}</p>
                  <p className="text-xs text-muted-foreground">{(b.contexts || []).map((c) => PRICE_CONTEXTS.find((x) => x[0] === c)?.[1] || c).join(", ") || "No channels selected"}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => del(b.id)} data-testid={`delete-band-${b.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-xl border border-border p-4 space-y-3">
          <p className="text-sm font-semibold">New price band</p>
          <Input placeholder="Band name (e.g. Dine-in menu)" value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="band-name" />
          <div className="grid grid-cols-2 gap-2">
            {PRICE_CONTEXTS.map(([c, label]) => (
              <label key={c} className="flex items-center gap-2 text-sm">
                <Checkbox checked={contexts.includes(c)} onCheckedChange={() => toggleCtx(c)} data-testid={`band-ctx-${c}`} /> {label}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} data-testid="band-default" /> Use as default for any channel not covered above
          </label>
          <Button className="h-11 w-full gap-1" onClick={add} data-testid="add-price-band"><Plus className="h-4 w-4" /> Add band</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ItemDialog({ rid, cats, allergens, bands = [], item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickImage = (files) => { if (files?.length) setCropFile(files[0]); };
  const generatePhoto = async () => {
    if (!item?.id) return toast.error("Save the item first, then generate a photo");
    setGenImg(true);
    try {
      const { data } = await api.post(`/restaurants/${rid}/menu/items/${item.id}/photo`);
      up("image_url", data.image_url);
      toast.success("AI photo generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate photo"); }
    setGenImg(false);
  };
  const uploadCropped = async (blob) => {
    setCropFile(null);
    setUploadingImg(true);
    try { const [url] = await uploadFiles(rid, [blob]); up("image_url", url); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploadingImg(false);
  };

  useEffect(() => {
    if (open) setF(item ? { ...empty, ...item, category_id: item.category_id || "" } : { ...empty, category_id: cats[0]?.id || "" });
  }, [open, item, cats]);

  const linkFor = (aid) => f.allergen_links.find((l) => l.allergen_id === aid);
  const toggleAllergen = (aid, checked) => {
    setF((p) => ({ ...p, allergen_links: checked
      ? [...p.allergen_links, { allergen_id: aid, relation_type: "CONTAINS" }]
      : p.allergen_links.filter((l) => l.allergen_id !== aid) }));
  };
  const setRelation = (aid, rel) => setF((p) => ({ ...p, allergen_links: p.allergen_links.map((l) => l.allergen_id === aid ? { ...l, relation_type: rel } : l) }));

  const addOption = () => setF((p) => ({ ...p, options: [...(p.options || []), { name: "", type: "SINGLE_SELECT", required: false, values: [] }] }));
  const updateOption = (oi, key, val) => setF((p) => ({ ...p, options: p.options.map((o, i) => i === oi ? { ...o, [key]: val } : o) }));
  const removeOption = (oi) => setF((p) => ({ ...p, options: p.options.filter((_, i) => i !== oi) }));
  const addValue = (oi) => setF((p) => ({ ...p, options: p.options.map((o, i) => i === oi ? { ...o, values: [...(o.values || []), { name: "", price_modifier: 0, is_available: true }] } : o) }));
  const updateValue = (oi, vi, key, val) => setF((p) => ({ ...p, options: p.options.map((o, i) => i === oi ? { ...o, values: o.values.map((v, j) => j === vi ? { ...v, [key]: val } : v) } : o) }));
  const removeValue = (oi, vi) => setF((p) => ({ ...p, options: p.options.map((o, i) => i === oi ? { ...o, values: o.values.filter((_, j) => j !== vi) } : o) }));

  const save = async () => {
    if (!f.name) return toast.error("Enter a name");
    const cleanBands = {};
    Object.entries(f.band_prices || {}).forEach(([bid, v]) => {
      if (v !== "" && v !== null && v !== undefined && !isNaN(+v)) cleanBands[bid] = +v;
    });
    const body = { ...f, price: +f.price, prep_time_minutes: +f.prep_time_minutes,
      promotion_price: f.promotion_price === "" ? null : +f.promotion_price,
      subcategory: f.subcategory || null, band_prices: cleanBands,
      options: (f.options || []).map((o) => ({ ...o, values: (o.values || []).map((v) => ({ ...v, price_modifier: +v.price_modifier || 0 })) })) };
    try {
      if (item) await api.put(`/menu/items/${item.id}`, body);
      else await api.post(`/restaurants/${rid}/menu/items`, body);
      toast.success("Menu item saved"); setOpen(false); onSaved();
    } catch { toast.error("Could not save item"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit" : "Add"} menu item</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Item photo</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {f.image_url
                ? <img src={fileUrl(f.image_url)} alt="item" className="h-16 w-16 rounded-lg object-cover border border-border" data-testid="item-image-preview" />
                : <div className="h-16 w-16 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground"><ImageIcon className="h-5 w-5" /></div>}
              <label className="cursor-pointer text-sm text-primary font-medium">
                <input type="file" accept="image/*" className="hidden" data-testid="item-image-upload" onChange={(e) => pickImage(e.target.files)} />
                {uploadingImg ? "Uploading…" : (f.image_url ? "Change photo" : "Upload photo")}
              </label>
              {f.image_url && <button type="button" onClick={() => up("image_url", "")} className="text-xs text-red-500">Remove</button>}
              {item?.id && (
                <button type="button" onClick={generatePhoto} disabled={genImg} data-testid="item-generate-photo"
                  className="text-xs text-primary font-medium inline-flex items-center gap-1 disabled:opacity-50">
                  <Sparkles className="h-3.5 w-3.5" /> {genImg ? "Generating…" : "Generate with AI"}
                </button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Images are cropped to a 4:3 tile for a consistent menu look.</p>
          </div>
          <div className="sm:col-span-2"><Label>Name</Label><Input className="h-11 mt-1.5" value={f.name} onChange={(e) => up("name", e.target.value)} data-testid="item-name" /></div>
          <div className="sm:col-span-2"><Label>Description</Label><Textarea className="mt-1.5" value={f.description || ""} onChange={(e) => up("description", e.target.value)} /></div>
          <div><Label>Price (£)</Label><Input type="number" step="0.01" className="h-11 mt-1.5" value={f.price} onChange={(e) => up("price", e.target.value)} data-testid="item-price" /></div>
          <div><Label>Category</Label>
            <Select value={f.category_id} onValueChange={(v) => up("category_id", v)}>
              <SelectTrigger className="h-11 mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Prep time (min)</Label><Input type="number" className="h-11 mt-1.5" value={f.prep_time_minutes} onChange={(e) => up("prep_time_minutes", e.target.value)} /></div>
          <div><Label>Subcategory (optional)</Label><Input className="h-11 mt-1.5" value={f.subcategory || ""} onChange={(e) => up("subcategory", e.target.value)} placeholder="e.g. Pizzas, Warm desserts" data-testid="item-subcategory" /></div>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch checked={f.is_available} onCheckedChange={(v) => up("is_available", v)} data-testid="item-available" />
            <span className="text-sm">Available on menu</span>
          </div>
        </div>

        <div className="rounded-xl border border-border p-4 space-y-3">
          <p className="text-sm font-semibold">AI selling</p>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={f.is_popular} onCheckedChange={(v) => up("is_popular", v)} data-testid="item-popular" />
              <span className="text-sm">Popular dish</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={f.is_cross_sell} onCheckedChange={(v) => up("is_cross_sell", v)} data-testid="item-cross-sell" />
              <span className="text-sm">Suggest as cross-sell</span>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={f.is_promotion} onCheckedChange={(v) => up("is_promotion", v)} data-testid="item-promotion" />
              <span className="text-sm">Promotion</span>
            </div>
          </div>
          {f.is_promotion && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Promotion text</Label><Input className="h-11 mt-1.5" value={f.promotion_text || ""} onChange={(e) => up("promotion_text", e.target.value)} placeholder="e.g. 2 for £8" data-testid="item-promotion-text" /></div>
              <div><Label>Promotion price (£, optional)</Label><Input type="number" step="0.01" className="h-11 mt-1.5" value={f.promotion_price} onChange={(e) => up("promotion_price", e.target.value)} /></div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Options & add-ons</p>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addOption} data-testid="add-option-group">
              <Plus className="h-3.5 w-3.5" /> Add option group
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">e.g. a "Spice level" (single choice) or "Add-ons" (multiple choice, with extra prices). The AI only offers these — it never invents sizes or options.</p>
          {(f.options || []).map((o, oi) => (
            <div key={oi} className="rounded-lg border border-border p-3 space-y-2" data-testid={`option-group-${oi}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Input className="h-10 flex-1 min-w-[140px]" placeholder="Group name (e.g. Spice level)" value={o.name} onChange={(e) => updateOption(oi, "name", e.target.value)} data-testid={`option-name-${oi}`} />
                <Select value={o.type} onValueChange={(v) => updateOption(oi, "type", v)}>
                  <SelectTrigger className="h-10 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SINGLE_SELECT">Single choice</SelectItem>
                    <SelectItem value="MULTI_SELECT">Multiple choice</SelectItem>
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-sm"><Switch checked={o.required} onCheckedChange={(v) => updateOption(oi, "required", v)} /> Required</label>
                <Button type="button" size="icon" variant="ghost" onClick={() => removeOption(oi)} data-testid={`remove-option-${oi}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
              <div className="space-y-2 pl-1">
                {(o.values || []).map((v, vi) => (
                  <div key={vi} className="flex items-center gap-2">
                    <Input className="h-9 flex-1" placeholder="Choice (e.g. Hot)" value={v.name} onChange={(e) => updateValue(oi, vi, "name", e.target.value)} data-testid={`option-value-${oi}-${vi}`} />
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-muted-foreground">+£</span>
                      <Input type="number" step="0.01" className="h-9 w-24" placeholder="0.00" value={v.price_modifier} onChange={(e) => updateValue(oi, vi, "price_modifier", e.target.value)} data-testid={`option-price-${oi}-${vi}`} />
                    </div>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeValue(oi, vi)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                ))}
                <Button type="button" variant="ghost" size="sm" className="gap-1" onClick={() => addValue(oi)} data-testid={`add-value-${oi}`}><Plus className="h-3.5 w-3.5" /> Add choice</Button>
              </div>
            </div>
          ))}
        </div>

        {bands.length > 0 && (
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="item-band-pricing">
            <p className="text-sm font-semibold">Channel pricing</p>
            <p className="text-xs text-muted-foreground">Set a price per band. Leave blank to use the base price ({GBP(+f.price || 0)}).</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {bands.map((b) => (
                <div key={b.id}>
                  <Label className="text-xs">{b.name}</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-sm text-muted-foreground">£</span>
                    <Input type="number" step="0.01" className="h-10" placeholder={(+f.price || 0).toFixed(2)}
                      value={(f.band_prices || {})[b.id] ?? ""} data-testid={`band-price-${b.id}`}
                      onChange={(e) => setF((p) => ({ ...p, band_prices: { ...(p.band_prices || {}), [b.id]: e.target.value } }))} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <Label className="mb-2 block">Declared allergens</Label>
          <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto rounded-xl border border-border p-3">
            {allergens.map((a) => {
              const link = linkFor(a.id);
              return (
                <div key={a.id} className="flex items-center gap-2">
                  <Checkbox checked={!!link} onCheckedChange={(c) => toggleAllergen(a.id, c)} data-testid={`allergen-${a.id}`} />
                  <span className="text-sm flex-1">{a.name}</span>
                  {link && (
                    <Select value={link.relation_type} onValueChange={(v) => setRelation(a.id, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{ALLERGEN_RELATIONS.map((r) => <SelectItem key={r} value={r}>{pretty(r)}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <DialogFooter><Button className="h-11" onClick={save} data-testid="save-item">Save item</Button></DialogFooter>
      </DialogContent>
      {cropFile && <ImageCropper file={cropFile} onCancel={() => setCropFile(null)} onCropped={uploadCropped} />}
    </Dialog>
  );
}

function AutoPhotosButton({ rid, missingCount, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (missingCount === 0) return toast.info("Every item already has a photo");
    if (!window.confirm(`Generate AI photos for ${missingCount} item${missingCount === 1 ? "" : "s"} without a photo? A small wallet fee applies per photo.`)) return;
    setBusy(true);
    let done = 0;
    try {
      for (let i = 0; i < 20; i++) {
        const { data } = await api.post(`/restaurants/${rid}/menu/auto-photos`, { max: 15 });
        done += data.generated || 0;
        onDone();
        if (data.generated) toast.success(`Generated ${done} photo${done === 1 ? "" : "s"}…${data.remaining ? ` ${data.remaining} to go` : ""}`);
        if (!data.remaining || !data.generated) break;
      }
      toast.success(done > 0 ? `Done — created ${done} dish photos` : "No photos generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate photos");
    }
    setBusy(false);
  };
  return (
    <Button variant="outline" className="h-11 gap-2" disabled={busy} onClick={run} data-testid="auto-photos-btn">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
      {busy ? "Generating…" : `Auto photos${missingCount ? ` (${missingCount})` : ""}`}
    </Button>
  );
}

export default function Menu() {
  const { rid } = useParams();
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [bands, setBands] = useState([]);
  const [newCat, setNewCat] = useState("");

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/menu/categories`).then((r) => setCats(r.data));
    api.get(`/restaurants/${rid}/menu/items`).then((r) => setItems(r.data));
    api.get(`/restaurants/${rid}/price-bands`).then((r) => setBands(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); api.get("/allergens").then((r) => setAllergens(r.data)); }, [load]);

  const addCat = async () => {
    if (!newCat) return;
    await api.post(`/restaurants/${rid}/menu/categories`, { name: newCat, sort_order: cats.length });
    setNewCat(""); load(); toast.success("Category added");
  };
  const delItem = async (id) => { await api.delete(`/menu/items/${id}`); load(); toast.success("Item removed"); };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const itemsByCat = (cid) => items.filter((i) => i.category_id === cid);

  const onCategoryDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(cats, cats.findIndex((c) => c.id === active.id), cats.findIndex((c) => c.id === over.id));
    setCats(next);
    try { await api.put(`/restaurants/${rid}/menu/categories/reorder`, next.map((c) => c.id)); toast.success("Category order saved"); }
    catch { toast.error("Could not save order"); load(); }
  };

  const onItemDragEnd = (cid) => async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = itemsByCat(cid);
    const nextList = arrayMove(list, list.findIndex((i) => i.id === active.id), list.findIndex((i) => i.id === over.id));
    const q = [...nextList];
    setItems((prev) => prev.map((i) => (i.category_id === cid ? q.shift() : i)));
    try { await api.put(`/restaurants/${rid}/menu/items/reorder`, nextList.map((i) => i.id)); toast.success("Item order saved"); }
    catch { toast.error("Could not save order"); load(); }
  };

  return (
    <div>
      <PageHeader title="Menu & allergens" subtitle="Drag the handles to reorder — this controls the order the AI offers categories and items.">
        <PriceBandsManager rid={rid} bands={bands} onChanged={load} />
        <AutoPhotosButton rid={rid} missingCount={items.filter((i) => !i.image_url).length} onDone={load} />
        <MenuScanDialog rid={rid} onImported={load}
          trigger={<Button variant="outline" className="h-11 gap-2" data-testid="import-menu-photo"><Sparkles className="h-4 w-4" /> Import from photo</Button>} />
        <ItemDialog rid={rid} cats={cats} allergens={allergens} bands={bands} onSaved={load}
          trigger={<Button className="h-11 gap-2" data-testid="add-item-button"><Plus className="h-4 w-4" /> Add item</Button>} />
      </PageHeader>

      <Card className="p-4 rounded-2xl mb-6 flex items-center gap-2">
        <Input placeholder="New category name" value={newCat} onChange={(e) => setNewCat(e.target.value)} className="h-11 max-w-xs" data-testid="new-category-input" />
        <Button variant="outline" className="h-11" onClick={addCat} data-testid="add-category-button">Add category</Button>
      </Card>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCategoryDragEnd}>
        <SortableContext items={cats.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {cats.map((c) => (
            <SortableCategory key={c.id} id={c.id} name={c.name}>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onItemDragEnd(c.id)}>
                <SortableContext items={itemsByCat(c.id).map((i) => i.id)} strategy={rectSortingStrategy}>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {itemsByCat(c.id).map((i) => (
                      <SortableMenuItem key={i.id} id={i.id}>
                        <Card data-testid={`menu-item-${i.id}`} className="p-5 pt-6 rounded-2xl">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-display font-bold">{i.name}</p>
                              {i.subcategory && <p className="text-xs text-muted-foreground">{i.subcategory}</p>}
                              <p className="text-sm text-muted-foreground line-clamp-1">{i.description}</p>
                            </div>
                            <span className="font-display font-bold">{GBP(i.price)}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {i.is_promotion && <Badge variant="outline" className="bg-accent/15 text-accent border-accent/30 gap-1"><Tag className="h-3 w-3" /> {i.promotion_text || "Promotion"}</Badge>}
                            {i.is_popular && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1"><Star className="h-3 w-3" /> Popular</Badge>}
                            {i.is_cross_sell && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1"><Sparkles className="h-3 w-3" /> Cross-sell</Badge>}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {i.allergen_links?.map((l) => {
                              const a = allergens.find((x) => x.id === l.allergen_id);
                              return <Badge key={l.allergen_id} variant="outline" className="text-xs">{a?.name}</Badge>;
                            })}
                            {!i.is_available && <Badge variant="outline" className="bg-stone-100 text-stone-500">Hidden</Badge>}
                          </div>
                          <div className="mt-4 flex gap-2 pt-3 border-t border-border">
                            <ItemDialog rid={rid} cats={cats} allergens={allergens} bands={bands} item={i} onSaved={load}
                              trigger={<Button variant="outline" size="sm" className="gap-1" data-testid={`edit-item-${i.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>} />
                            <Button variant="ghost" size="sm" className="gap-1 text-red-600" onClick={() => delItem(i.id)} data-testid={`delete-item-${i.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                          </div>
                        </Card>
                      </SortableMenuItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </SortableCategory>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}
